import { Log } from '@aboutyou/legacy-shared'
import { unescape } from '@aboutyou/legacy-shared/src/utils/url-utils'
import { ELASTIC_SEARCH_MAXIMUM_PAGE } from 'constants/pagination'
import { SHOP_SIZE_RUNS } from 'constants/quickFilter'
import { exists } from 'helpers/exists'
import {
  adjustFilterValueBounds,
  extractFilterFromQuery,
  extractNoneFilterFromQuery,
  intersectAvailableFilters,
  isFiltersFittingValueBounds,
  normalizeFilterFromQuery,
} from 'helpers/filter'
import { getValidGender } from 'helpers/gender'
import { redirect } from 'helpers/navigation'
import { FetchProps } from 'helpers/redial'
import { extractSortIdFromQuery, getSortChannel } from 'helpers/sort'
import { showProductStreamErrorToast } from 'helpers/toasts'
import { transaction } from 'mobx'
import { getAvailableFiltersWithAvailableAndSelectedValues } from 'pages/Category/helpers/getAvailableFiltersWithAvailableAndSelectedValues'
import { FilterItem } from 'services/providers/bapi/entities/filters/types'
import shallowEqual from 'shallowequal'
import { createProductStreamFetcher } from 'sharedComponents/ProductStream/loadingHelpers'
import ImageViewStore from 'store/features/imageView'
import { SelectedLooseFilterMapType } from 'store/sidecar/FilterViewStore'
import PaginationViewStore from 'store/sidecar/PaginationViewStore'

export default {
  fetch: async ({ store, service, query, pathname }: FetchProps) => {
    await store.layoutStore.fetchNavigation()
    const currentPage = query.page ? parseInt(query.page, 10) : 1
    const rootId = store.layoutStore.getMappedCategoryId(
      'root',
      getValidGender(query.gender),
    ) as string

    const currentCategoryId = query.category || rootId
    const currentTerm = unescape(query.term)
    const sortId = extractSortIdFromQuery(query)

    const enableShopSizeRunFilters = store.feature.isActive(
      'enableShopSizeRunFilters',
    )

    store.searchPage.startLoading()

    const currentCategory = await service.categories.requestByCategoryId(
      currentCategoryId,
      {
        strategy: 'cache_or_network',
      },
    )

    const categoryAvailableFilterNames = new Set(
      currentCategory.supportedFilter.concat('term'),
    )

    const queryFilter = extractFilterFromQuery(
      query,
      categoryAvailableFilterNames,
      [],
      {
        unescapeValues: true,
      },
    )

    const queryNoneFilter = extractNoneFilterFromQuery(
      query,
      categoryAvailableFilterNames,
    )
    const selectedFilters = normalizeFilterFromQuery(
      queryFilter,
      categoryAvailableFilterNames,
    )
    const pageIsFiltered = Object.keys(selectedFilters).length >= 2

    const shouldTryPageTypeRedirect =
      !pageIsFiltered && rootId === currentCategoryId && currentPage <= 1
    let shouldShowToastError = false

    if (!currentTerm) {
      throw redirect(
        '/',
        {},
        {
          replaceState: true,
        },
      )
    }

    if (!shallowEqual(queryFilter, selectedFilters)) {
      throw redirect(
        pathname,
        { ...queryNoneFilter, ...selectedFilters },
        {
          replaceState: true,
        },
      )
    }

    if (currentPage > ELASTIC_SEARCH_MAXIMUM_PAGE || currentPage < 0) {
      throw redirect(
        pathname,
        { ...queryNoneFilter, ...selectedFilters, page: undefined },
        {
          replaceState: true,
        },
      )
    }

    if (ImageViewStore.isImageType(query.preferredProductImage)) {
      store.imageView.updateUserImageType(query.preferredProductImage)
    }

    store.searchPage.filter.updateSelected(selectedFilters)

    let requestGender =
      query.gender && query.gender !== store.user.gender
        ? store.user.setGenderAndLoadNavigation(query.gender, {
            enforceOnClient: true,
          })
        : Promise.resolve()

    const availableFilterRequest: Promise<FilterItem[]> = (() => {
      const { minPrice, maxPrice, ...selectedWithoutPricesFilters } =
        selectedFilters
      return service.filters
        .requestByCategoryId(
          currentCategoryId,
          {
            selectedFilters: selectedWithoutPricesFilters,
            including: enableShopSizeRunFilters ? [SHOP_SIZE_RUNS] : undefined,
          },
          { strategy: 'cache_or_network' },
        )
        .catch(() => [])
    })()

    const availableCategoryIdsRequest = service.filters
      .requestActiveCategoryIds(
        rootId,
        { term: [currentTerm] },
        { strategy: 'cache_or_network' },
      )
      .catch(() => undefined)

    const currentBatch = PaginationViewStore.pageToFirstBatch(currentPage)

    const sortChannel = getSortChannel({
      selectedFilters,
      sortId,
    })

    const selectedFiltersForRequest: SelectedLooseFilterMapType = {
      ...selectedFilters,
      secondHand: ['false'],
    }
    const fetchProducts = createProductStreamFetcher({
      categoryId: currentCategoryId,
      service,
      sortId,
      sortChannel,
      filter: selectedFiltersForRequest,
      score: store.searchPage.sorting.score,
      onError: e => {
        shouldShowToastError = true
        Log.warn(
          `Error while fetching products on search page for category ${currentCategoryId}`,
          { error: e },
        )
      },
    })

    const { pagination, productIds, products } = await fetchProducts({
      perPage: PaginationViewStore.productsPerBatch,
      page: currentBatch,
    })

    if (currentPage > pagination.last && !shouldShowToastError) {
      throw redirect(
        pathname,
        { ...queryNoneFilter, ...selectedFilters, page: undefined },
        {
          replaceState: true,
        },
      )
    }

    // drop filters that are not relevant/ available for this category
    if (productIds.length === 0 && pageIsFiltered) {
      const allAvailableFilterByCategory =
        await service.filters.requestByCategoryId(
          currentCategoryId,
          {
            selectedFilters: {
              term: [currentTerm],
            },
            including: enableShopSizeRunFilters ? [SHOP_SIZE_RUNS] : undefined,
          },
          { strategy: 'cache_or_network' },
        )
      const acceptedFilters = intersectAvailableFilters(
        selectedFilters,
        allAvailableFilterByCategory,
      )
      if (!shallowEqual(acceptedFilters, selectedFilters)) {
        throw redirect(
          pathname,
          { ...queryNoneFilter, ...acceptedFilters },
          {
            replaceState: true,
          },
        )
      }
    }

    const availableFilters = await availableFilterRequest
    const finalAvailableFilters =
      await getAvailableFiltersWithAvailableAndSelectedValues(
        availableFilters,
        selectedFilters,
        {
          loadAttributesByFilterNames:
            service.filters.requestAttributesByFilterNames,
        },
      )

    // render empty product page
    if (productIds.length === 0) {
      transaction(() => {
        store.searchPage.reset({
          productIds: [],
        })
        store.searchPage.filter.reset({
          categoryId: currentCategoryId,
          selectedFilters: selectedFiltersForRequest,
          appliedSelectedLooseFilters: selectedFiltersForRequest,
        })
        store.searchPage.pagination.reset({
          currentPage,
          total: 0,
          totalPages: 0,
        })
        store.searchPage.category.reset(currentCategory)
        store.searchPage.endLoading()
        if (shouldShowToastError) {
          showProductStreamErrorToast(store.toastNotifications, store.t)
        }
      })
      return
    }

    if (productIds.length === 1 && shouldTryPageTypeRedirect) {
      const product = products[0]
      throw redirect(
        product.url,
        {},
        {
          replaceState: true,
        },
      )
    }

    // adjust filters in case they don't fit into the category bounds
    if (!isFiltersFittingValueBounds(selectedFilters, availableFilters)) {
      const adjustedFilters = adjustFilterValueBounds(
        selectedFilters,
        availableFilters,
      )
      throw redirect(
        pathname,
        { ...queryNoneFilter, ...adjustedFilters },
        {
          replaceState: true,
        },
      )
    }
    const availableCategoryIds = await availableCategoryIdsRequest

    const categoryRootCategory = currentCategory.rootlineIds![0]
    if (categoryRootCategory !== rootId) {
      const gender = (() => {
        switch (categoryRootCategory) {
          case store.layoutStore.femaleCategoryId:
            return 'female'
          case store.layoutStore.maleCategoryId:
            return 'male'
          case store.layoutStore.kidsCategoryId:
            return 'kids'
          default:
            throw new Error(
              `Cannot map category root category id to gender for category with id \`${currentCategory.id}\``,
            )
        }
      })()
      requestGender = store.user.setGenderAndLoadNavigation(gender, {
        enforceOnClient: true,
      })
    }

    await requestGender

    transaction(() => {
      store.searchPage.reset({
        productIds,
      })
      store.searchPage.filter.reset({
        categoryId: currentCategoryId,
        availableFilters: finalAvailableFilters,
        activeCategoryIds: availableCategoryIds,
        selectedFilters: selectedFiltersForRequest,
        appliedSelectedLooseFilters: selectedFiltersForRequest,
      })
      store.searchPage.category.reset(currentCategory)
      store.searchPage.pagination.reset({
        currentPage,
        total: pagination.total,
        totalPages: PaginationViewStore.totalBatchesToTotalPages(
          pagination.last,
        ),
      })
      store.searchPage.sorting.reset({
        appliedProductsSortingId: sortId,
        sortChannel,
      })

      const categoriesForBreadcrumbs = currentCategory
        .rootlineIds!.map(rootlineId => service.categories.getById(rootlineId))
        .filter(exists)

      store.breadcrumbsStore.setBreadCrumbs(categoriesForBreadcrumbs)
      if (shouldShowToastError) {
        showProductStreamErrorToast(store.toastNotifications, store.t)
      }
      store.searchPage.endLoading()
    })
  },

  deferLazy({ store, service }: FetchProps) {
    store.tracking.trackBreadCrumbs()
    if (store.searchPage.pagination.totalPages === 0) {
      service.recommendations.requestTopProductsByCategoryId(
        store.layoutStore.rootCategoryId,
      )
    }
  },
}
